import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboard, cuentas, movimientos } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { exportFlujoCajaToPDF } from '../lib/pdfExport';
import { toast } from 'sonner';
import { 
  Wallet,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  FileDown,
  Calendar,
  Loader2
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  ReferenceLine
} from 'recharts';

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="custom-tooltip">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} style={{ color: entry.color }} className="text-sm">
            {entry.name}: {formatCurrency(entry.value)}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function FlujoCaja() {
  const [loading, setLoading] = useState(true);
  const [cuentasList, setCuentasList] = useState([]);
  const [selectedCuenta, setSelectedCuenta] = useState('consolidado');
  const [flujoData, setFlujoData] = useState([]);
  
  // Date filters - Dynamic based on actual data
  const [selectedYear, setSelectedYear] = useState(() => String(new Date().getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(() => String(new Date().getMonth() + 1).padStart(2, '0'));
  const [selectedMes, setSelectedMes] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [selectedPeriodo, setSelectedPeriodo] = useState('6'); // Default to 6 months for cash flow
  
  // Dynamic date range from actual movements
  const [availableYears, setAvailableYears] = useState([]);

  // Period options
  const periodoOptions = [
    { value: '3', label: '3 Meses' },
    { value: '6', label: '6 Meses' },
    { value: '12', label: '12 Meses' },
  ];

  // Month names in Spanish
  const monthNames = [
    { value: '01', label: 'Enero' },
    { value: '02', label: 'Febrero' },
    { value: '03', label: 'Marzo' },
    { value: '04', label: 'Abril' },
    { value: '05', label: 'Mayo' },
    { value: '06', label: 'Junio' },
    { value: '07', label: 'Julio' },
    { value: '08', label: 'Agosto' },
    { value: '09', label: 'Septiembre' },
    { value: '10', label: 'Octubre' },
    { value: '11', label: 'Noviembre' },
    { value: '12', label: 'Diciembre' },
  ];

  // Update selectedMes when year or month changes
  useEffect(() => {
    setSelectedMes(`${selectedYear}-${selectedMonth}`);
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    loadCuentas();
    loadDateRange();
  }, []);

  useEffect(() => {
    loadFlujoData();
  }, [selectedCuenta, selectedMes, selectedPeriodo]);

  const loadDateRange = async () => {
    try {
      const response = await movimientos.getRangoFechas();
      if (response.data?.anios_disponibles) {
        setAvailableYears(response.data.anios_disponibles);
        if (response.data.anios_disponibles.length > 0) {
          const mostRecentYear = response.data.anios_disponibles[0];
          setSelectedYear(mostRecentYear);
        }
      }
    } catch (error) {
      console.error('Error loading date range:', error);
    }
  };

  const loadCuentas = async () => {
    try {
      const response = await cuentas.getAll();
      setCuentasList(response.data);
    } catch (error) {
      console.error('Error loading accounts:', error);
    }
  };

  const loadFlujoData = async () => {
    setLoading(true);
    try {
      const params = selectedCuenta === 'consolidado' 
        ? { consolidado: true, meses: parseInt(selectedPeriodo), mes_base: selectedMes } 
        : { cuenta_id: selectedCuenta, meses: parseInt(selectedPeriodo), mes_base: selectedMes };

      const response = await dashboard.getFlujoCaja(params);
      setFlujoData(response.data);
    } catch (error) {
      console.error('Error loading cash flow:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentMonth = flujoData[flujoData.length - 1] || {};
  const totalIngresos = flujoData.reduce((sum, m) => sum + m.ingresos, 0);
  const totalEgresos = flujoData.reduce((sum, m) => sum + m.egresos, 0);
  const saldoFinal = currentMonth.saldo_acumulado || 0;

  if (loading) {
    return (
      <div className="p-8">
        <div className="kpi-grid mb-8">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="skeleton h-32 rounded-xl"></div>
          ))}
        </div>
        <div className="skeleton h-80 rounded-xl"></div>
      </div>
    );
  }

  return (
    <div data-testid="flujo-caja-page">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Flujo de Caja
            </h1>
            <p className="text-muted-foreground mt-1">
              Visualiza el flujo de efectivo de tu empresa
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {/* Year Selector */}
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-20" data-testid="year-selector">
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                {availableYears.length > 0 ? (
                  availableYears.map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))
                ) : (
                  Array.from({ length: 10 }, (_, i) => String(new Date().getFullYear() - i)).map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            
            {/* Month Selector - Show for 3 and 6 months, hide for 12 (full year) */}
            {selectedPeriodo !== '12' && (
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-32" data-testid="month-selector">
                  <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
                  <SelectValue placeholder="Mes base" />
                </SelectTrigger>
                <SelectContent>
                  {monthNames.map((month) => (
                    <SelectItem key={month.value} value={month.value}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Period Selector */}
            <Select value={selectedPeriodo} onValueChange={setSelectedPeriodo}>
              <SelectTrigger className="w-28" data-testid="periodo-selector">
                <SelectValue placeholder="Periodo" />
              </SelectTrigger>
              <SelectContent>
                {periodoOptions.map((periodo) => (
                  <SelectItem key={periodo.value} value={periodo.value}>
                    {periodo.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Cuenta Selector */}
            <Select value={selectedCuenta} onValueChange={setSelectedCuenta}>
              <SelectTrigger className="w-36" data-testid="cuenta-selector">
                <SelectValue placeholder="Cuenta" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="consolidado">Consolidado</SelectItem>
                {cuentasList.map((cuenta) => (
                  <SelectItem key={cuenta.id} value={cuenta.id}>
                    {cuenta.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                const kpisData = {
                  total_ingresos: flujoData.reduce((s, f) => s + (f.ingresos || 0), 0),
                  total_egresos: flujoData.reduce((s, f) => s + (f.egresos || 0), 0),
                  flujo_neto: flujoData.reduce((s, f) => s + (f.flujo_neto || 0), 0),
                  promedio_mensual: flujoData.length > 0 ? flujoData.reduce((s, f) => s + (f.flujo_neto || 0), 0) / flujoData.length : 0
                };
                exportFlujoCajaToPDF(kpisData, flujoData);
                toast.success('PDF generado exitosamente');
              }}
              data-testid="export-pdf-btn"
            >
              <FileDown className="w-4 h-4 mr-2" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="page-content">
        {/* Summary Cards */}
        <div className="kpi-grid mb-8">
          <Card className="kpi-card bg-emerald-50 border-emerald-200">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-emerald-600">Ingresos (6 meses)</p>
                  <p className="text-2xl font-bold text-emerald-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalIngresos)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-100">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="kpi-card bg-red-50 border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-red-600">Egresos (6 meses)</p>
                  <p className="text-2xl font-bold text-red-700" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(totalEgresos)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-red-100">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="kpi-card">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Flujo Mes Actual</p>
                  <p className={`text-2xl font-bold ${currentMonth.flujo_neto >= 0 ? 'text-emerald-600' : 'text-red-600'}`} 
                     style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(currentMonth.flujo_neto || 0)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-muted">
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="kpi-card bg-primary/5 border-primary/20">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-primary">Saldo Acumulado</p>
                  <p className={`text-2xl font-bold ${saldoFinal >= 0 ? 'text-primary' : 'text-red-600'}`} 
                     style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {formatCurrency(saldoFinal)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-primary/10">
                  <Wallet className="w-5 h-5 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts */}
        <div className="charts-grid">
          {/* Flow Chart */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Flujo de Efectivo Mensual
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={flujoData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="mes_nombre" tick={{ fontSize: 12 }} stroke="#64748B" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748B" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="#64748B" />
                    <Bar 
                      dataKey="flujo_neto" 
                      name="Flujo Neto"
                      radius={[4, 4, 0, 0]}
                    >
                      {flujoData.map((entry, index) => (
                        <rect 
                          key={`bar-${index}`} 
                          fill={entry.flujo_neto >= 0 ? '#10B981' : '#EF4444'} 
                        />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Accumulated Balance */}
          <Card className="chart-container">
            <CardHeader>
              <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Saldo Acumulado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={flujoData}>
                    <defs>
                      <linearGradient id="colorSaldo" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#BE123C" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#BE123C" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="mes_nombre" tick={{ fontSize: 12 }} stroke="#64748B" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#64748B" tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip content={<CustomTooltip />} />
                    <ReferenceLine y={0} stroke="#64748B" strokeDasharray="3 3" />
                    <Area
                      type="monotone"
                      dataKey="saldo_acumulado"
                      stroke="#BE123C"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorSaldo)"
                      name="Saldo Acumulado"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Details */}
        <Card className="mt-6 table-container">
          <CardHeader>
            <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Detalle Mensual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Mes</th>
                    <th className="text-right">Ingresos</th>
                    <th className="text-right">Egresos</th>
                    <th className="text-right">Flujo Neto</th>
                    <th className="text-right">Saldo Acumulado</th>
                  </tr>
                </thead>
                <tbody>
                  {flujoData.map((mes) => (
                    <tr key={mes.mes}>
                      <td className="font-medium">{mes.mes_nombre}</td>
                      <td className="text-right text-emerald-600">
                        {formatCurrency(mes.ingresos)}
                      </td>
                      <td className="text-right text-red-600">
                        {formatCurrency(mes.egresos)}
                      </td>
                      <td className={`text-right font-medium ${mes.flujo_neto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {mes.flujo_neto >= 0 ? '+' : ''}{formatCurrency(mes.flujo_neto)}
                      </td>
                      <td className={`text-right font-bold ${mes.saldo_acumulado >= 0 ? 'text-primary' : 'text-red-600'}`}>
                        {formatCurrency(mes.saldo_acumulado)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
